import { SelectableRole } from '@/types/role.type'

export enum USER_ROLES {
  User = 'user',
  Admin = 'admin',
  OrganizationOwner = 'organization_owner',
  OrganizationAdmin = 'organization_admin',
  OrganizationMember = 'organization_member',
  // Read-only analytics access; alpha-server grants it view_organization (ROLE_PERMISSIONS).
  OrganizationAnalyst = 'organization_analyst',
  // Shared(Vault) 업로드는 가능하되 관리(remove/resync/download/move/delete)는 불가한 위임 role.
  // member 대비 "Shared 업로드"만 추가된다. (PRD linq-alpha-client#3966)
  OrganizationContributor = 'organization_contributor',
  MeetingAgentExcludedUser = 'meeting_agent_excluded_user',
  MeetingAgentOrgVault = 'meeting_agent_org_vault',
  // Organization role: the workspace model chip names the resolved default model
  // instead of "Auto" (customers contracted to one default model, e.g. Groupama).
  WorkspaceModelShowDefaultName = 'workspace_model_show_default_name',
  MeetingAgentNoRecordUser = 'meeting_agent_no_record_user',
  MeetingAgentHideTranscript = 'meeting_agent_hide_transcript',
  MeetingAgentHideEmailTranscript = 'meeting_agent_hide_email_transcript',
  CompanyPageTester = 'company_page_tester',
  AnalyticsAgentTester = 'analytics_agent_tester',
  MeetingAgentCustomGuideUser = 'meeting_agent_custom_guide_user',
  MeetingAgentDisplayNameUser = 'meeting_agent_display_name_user',
  MeetingAgentCustomEmailUser = 'meeting_agent_custom_email_user',
  MeetingAgentComplianceViewer = 'meeting_agent_compliance_viewer',
  MeetingAgentComplianceTagger = 'meeting_agent_compliance_tagger',
  MeetingAgentComplianceAnalyst = 'meeting_agent_compliance_analyst',
  AnalyticsValutExcludedUser = 'analytics_valut_excluded_user',
  ModelSelectorUser = 'model_selector_user',
  WatchlistFileUploadDisabled = 'watchlist_file_upload_disabled',
  RecurringMeetingsManager = 'recurring_meetings_manager',
  // DEPRECATED: the Credit Agent page was removed from the client. Kept only so a
  // residual server-side assignment doesn't error — no client code branches on it.
  CreditAgent = 'credit_agent',
  // DEPRECATED (linq-alpha-server #3340): superseded by EnabledOntologySelector, which
  // gates the dynamic ontology-backed fund selector instead of the static funds.data.ts
  // list. Left in place only so any residual server-side role assignment doesn't error —
  // no client code branches on it anymore.
  EnabledHanwhaFundDropdown = 'enabled_hanwha_fund_dropdown',
  // Gates the Fund selector (dynamic, ontology-backed — #3340). Read-only access.
  EnabledOntologySelector = 'enabled_ontology_selector',
  // Gates create/edit/delete affordances within the Fund selector (#3340). Write access.
  EnabledOntologyWrite = 'enabled_ontology_write',
  // rms_v2에서 Google Drive 소스를 'Hanwha Files'로 화이트라벨(로고 숨김 + 라벨 변경)하는 대상 사용자.
  // 추가로 Shared에서 Vault direct upload를 제외한 모든 스토리지 조작을 차단한다(owner는 예외). (PRD #3966)
  Hanwha = 'hanwha_role',
  // 마스킹되지 않은 원본 파일 열람(Preview)을 차단하는 blacklist role (client-only 게이트). (PRD #3966)
  // 다운로드는 관여하지 않는다 — OriginalFileDownloadDisabled 가 독립적으로 담당한다.
  OriginalFileViewDisabled = 'original_file_view_disabled',
  // 원본 파일 다운로드를 차단하는 blacklist role (client-only 게이트).
  // OriginalFileViewDisabled 와 완전히 독립이다: 열람만 막고 싶으면 view, 다운로드만 막고 싶으면
  // download, 둘 다 막으려면 두 role을 모두 부여한다.
  OriginalFileDownloadDisabled = 'original_file_download_disabled',
  // Gates the Hanwha CIO Daily Brief "문서(Documents)" tab under Briefings & Alerts.
  // Scoped narrower than `hanwha_role` (all Hanwha users) — only holders see the tab.
  HanwhaMarketBriefing = 'hanwha_market_briefing',
  // Gates the Artifacts library menu/page — only holders see the GNB entry.
  EnabledArtifactPage = 'enabled_artifact_page',
  // Nothing checks this role any more: artifact chat is open to every user
  // (ESS-41). Kept until the role rows are cleaned up.
  EnabledArtifactChat = 'enabled_artifact_chat',
  // Gates the Company Deepdive LNB entry — only holders see the GNB button.
  // Page access (/company_deepdive) itself is NOT blocked by this role.
  EnabledCompanyDeepdive = 'enabled_company_deepdive',
  // LIN-721 — gates the Settings "Connectors" tab (Microsoft Teams). The
  // server independently enforces the same direct user role.
  EnabledMicrosoftTeamsConnector = 'enabled_microsoft_teams_connector',
  // LIN-1155 — the Outlook connector's own user role, separate from Teams so a
  // user can be granted one connector without the other.
  EnabledMicrosoftOutlookConnector = 'enabled_microsoft_outlook_connector',
  // LIN-1055/1056 — the Slack connector's own user role. Independent of the
  // Microsoft roles: either one opens the Connectors tab.
  EnabledSlackConnector = 'enabled_slack_connector',
  // Event Map V1 pilot gate (COV-306/307): the org (or user) role that exposes
  // the /event_map page and its LNB entry. Paired with the build-time
  // NEXT_PUBLIC_ENABLE_EVENT_MAP switch — both must be on.
  EventMap = 'event_map',
  // Org (or user) role for customers not licensed for S&P Capital IQ: the vendor
  // stays visible in the source pickers but is locked off and this client never
  // selects it. Web client only; server-side entitlements are a separate change.
  DataVendorSnpDisabled = 'data_vendor_snp_disabled',
}

export enum SHARED_ROLES {
  WorkflowAdmin = 'workflow_admin',
  CatalystAdmin = 'catalyst_admin',
  // Browser agent (CUA) permissions. Granted to an organization (all members) or a user; either
  // counts. Sign-in, TOTP and residential egress only mean something with CuaEnabled, and TOTP
  // also needs CuaSignIn. cua-server enforces them; the client only hides what would be refused.
  CuaEnabled = 'cua_enabled',
  CuaSignIn = 'cua_sign_in',
  CuaTotpStorage = 'cua_totp_storage',
  CuaResidentialEgress = 'cua_residential_egress',
}

/**
 * Storage Connector 접근 제어용 Role 상수
 *
 * 패턴: storage_{connector}_connectable_user
 * 현재: Role 기반 직접 체크 (임시)
 * 향후: Permission 기반 전환 예정 (상세: .claude/plan/RBAC_MIGRATION_PLAN.md)
 *
 * 참고: Gmail, S3, EDS는 비공개로 제외
 */
export const STORAGE_CONNECTOR_ROLES = {
  GoogleDrive: 'storage_google_drive_connectable_user',
  OneDrive: 'storage_onedrive_connectable_user',
  SharePoint: 'storage_sharepoint_connectable_user',
  SharePointApp: 'storage_sharepoint_app_connectable_user',
  Outlook: 'storage_outlook_connectable_user',
  OneNote: 'storage_onenote_connectable_user',
} as const

export enum PERMISSIONS {
  ManageOrganization = 'manage_organization',
  ViewOrganization = 'view_organization',
  ViewWorkflows = 'view_workflows',
  SummarizeCallTranscriptWorkflow = 'summarize_call_transcript_workflow',
  SummarizeSellSideReport = 'summarize_sell_side_report',
  OrgFilesManage = 'org_files_manage',
  OrgFilesAnalyze = 'org_files_analyze',
}

export const selectableRoles: SelectableRole[] = [
  {
    name: 'Member',
    value: USER_ROLES.OrganizationMember,
    description: 'Standard access to the entire team',
  },
  {
    name: 'Owner',
    value: USER_ROLES.OrganizationOwner,
    description: 'Admin-level access to the entire team',
  },
  {
    name: 'Admin',
    value: USER_ROLES.OrganizationAdmin,
    description: 'Admin-level access to the entire team',
  },
  {
    name: 'Contributor',
    value: USER_ROLES.OrganizationContributor,
    description: 'Can upload to shared storage, but cannot manage (remove/resync/download)',
  },
]
